import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import {
  shouldAppendStrokePoint,
  strokeToPolylinePoints
} from 'utils/footprint-drawing-helpers';
import {
  FOOTPRINT_CANVAS_OFFSET,
  footprintGridToPixel,
  screenToGridFeetRaw
} from 'utils/footprint-shapes-editing';
import { PositionType } from 'data/types';

type FootprintDrawSession = {
  pointerId: number;
  path: PositionType[];
};

type FootprintDrawingLayerProps = Readonly<{
  onStrokeComplete: (points: PositionType[]) => void;
  zoom: number;
  isLayoutImageSelected?: boolean;
  onLayoutImageDeselect?: () => void;
  onDoubleClick?: () => void;
  zIndexClass?: string;
}>;

export default function FootprintDrawingLayer({
  onStrokeComplete,
  zoom,
  isLayoutImageSelected = false,
  onLayoutImageDeselect,
  onDoubleClick,
  zIndexClass = 'z-[25]'
}: FootprintDrawingLayerProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const sessionRef = useRef<FootprintDrawSession | null>(null);
  const [drawPath, setDrawPath] = useState<PositionType[] | null>(null);

  const startDraw = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (sessionRef.current) return;
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (isLayoutImageSelected) {
        onLayoutImageDeselect?.();
      }
      event.preventDefault();
      event.stopPropagation();
      const svg = svgRef.current;
      if (!svg) return;
      svg.setPointerCapture(event.pointerId);
      const start = screenToGridFeetRaw(
        event.clientX,
        event.clientY,
        svg,
        zoom
      );
      sessionRef.current = { pointerId: event.pointerId, path: [start] };
      setDrawPath([start]);
    },
    [isLayoutImageSelected, onLayoutImageDeselect, zoom]
  );

  const moveDraw = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;
      const svg = svgRef.current;
      if (!svg) return;
      const point = screenToGridFeetRaw(
        event.clientX,
        event.clientY,
        svg,
        zoom
      );
      const last = session.path[session.path.length - 1];
      if (!shouldAppendStrokePoint(last, point)) return;
      session.path.push(point);
      setDrawPath([...session.path]);
    },
    [zoom]
  );

  const endDraw = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;
      sessionRef.current = null;
      setDrawPath(null);
      onStrokeComplete(session.path);
    },
    [onStrokeComplete]
  );

  const cursorPixel = drawPath?.length
    ? footprintGridToPixel(drawPath[drawPath.length - 1])
    : null;

  return (
    <svg
      ref={svgRef}
      className={clsx('absolute', zIndexClass)}
      style={{
        left: `-${FOOTPRINT_CANVAS_OFFSET}px`,
        top: `-${FOOTPRINT_CANVAS_OFFSET}px`,
        width: `${FOOTPRINT_CANVAS_OFFSET * 2}px`,
        height: `${FOOTPRINT_CANVAS_OFFSET * 2}px`,
        overflow: 'visible',
        pointerEvents: 'auto',
        cursor: 'crosshair',
        touchAction: 'none'
      }}
      onPointerDown={startDraw}
      onPointerMove={moveDraw}
      onPointerUp={endDraw}
      onPointerCancel={endDraw}
      onDoubleClick={onDoubleClick}
    >
      {drawPath && drawPath.length > 1 && (
        <polyline
          points={strokeToPolylinePoints(drawPath)}
          fill='rgba(18, 85, 139, 0.06)'
          stroke='#12558B'
          strokeWidth={2.5}
          strokeDasharray='6 4'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      )}
      {cursorPixel && (
        <circle
          cx={cursorPixel.x}
          cy={cursorPixel.y}
          r={4}
          fill='#12558B'
          opacity={0.35}
          style={{ pointerEvents: 'none' }}
        />
      )}
    </svg>
  );
}
